"use client";

/**
 * InsuraSurvey — branded renderer for PostHog API-type surveys.
 *
 * PostHog is the brain (templates, targeting, cohorts, % rollout, response
 * analytics); this component is the face. Create surveys in the PostHog
 * dashboard with type "API" — they render here in CRM styling instead of
 * PostHog's popover. Popover-type surveys are ignored on purpose (and
 * should never be created — see PostHogProvider).
 *
 * Response events use PostHog's canonical survey format ("survey shown" /
 * "survey dismissed" / "survey sent" with $survey_id + $survey_response*),
 * so the dashboard's results/NPS breakdowns work exactly as if PostHog had
 * rendered the survey itself.
 *
 * Placement: fixed bottom-right, above the collapsed dialer launcher, below
 * modals. One survey at a time; dismissed/answered surveys never re-show on
 * this device (localStorage guard) — PostHog's own targeting also stops
 * re-serving answered surveys across devices.
 */

import { useCallback, useEffect, useRef, useState } from "react";
import posthog from "posthog-js";
import { X, ChevronRight, MessageSquareHeart } from "lucide-react";

import { Button } from "@/app/_module/components/ui/button";
import { Textarea } from "@/app/_module/components/ui/textarea";
import { cn } from "@/app/_module/utils/utils";

// ── PostHog survey shapes (subset we render) ─────────────────────────────
interface SurveyQuestion {
  type: "open" | "rating" | "single_choice" | "multiple_choice" | "link";
  question: string;
  description?: string;
  // rating
  scale?: 3 | 5 | 7 | 10;
  display?: "number" | "emoji";
  lowerBoundLabel?: string;
  upperBoundLabel?: string;
  // choice
  choices?: string[];
  hasOpenChoice?: boolean;
  // link
  link?: string;
  buttonText?: string;
  optional?: boolean;
}

interface PostHogSurvey {
  id: string;
  name: string;
  type: string; // we only render 'api'
  questions: SurveyQuestion[];
  appearance?: { thankYouMessageHeader?: string; thankYouMessageDescription?: string };
}

const SEEN_KEY = (id: string) => `insuracentral_survey_seen_${id}`;
const SHOW_DELAY_MS = 6_000; // let the page settle before asking for opinions

const EMOJI_SCALES: Record<number, string[]> = {
  3: ["😞", "😐", "😄"],
  5: ["😞", "🙁", "😐", "🙂", "😄"],
};

export function InsuraSurvey() {
  const [survey, setSurvey] = useState<PostHogSurvey | null>(null);
  const [visible, setVisible] = useState(false);
  const [questionIndex, setQuestionIndex] = useState(0);
  const [responses, setResponses] = useState<Record<number, string | string[] | number>>({});
  const [openText, setOpenText] = useState("");
  const [multiPick, setMultiPick] = useState<Set<string>>(new Set());
  const [thanks, setThanks] = useState(false);
  const shownRef = useRef(false);

  // Fetch API-type surveys targeted at this user once PostHog is live.
  useEffect(() => {
    const timer = setTimeout(() => {
      try {
        if (!(posthog as any)?.__loaded) return;
        posthog.getActiveMatchingSurveys((surveys: any[]) => {
          const candidate = (surveys || []).find(
            (s) =>
              s.type === "api" &&
              Array.isArray(s.questions) &&
              s.questions.length > 0 &&
              !localStorage.getItem(SEEN_KEY(s.id)),
          );
          if (candidate) {
            setSurvey(candidate as PostHogSurvey);
            setVisible(true);
          }
        }, true);
      } catch {
        // PostHog unavailable (blocked, offline) — never break the app for a survey
      }
    }, SHOW_DELAY_MS);
    return () => clearTimeout(timer);
  }, []);

  // "survey shown" — once, when it actually appears
  useEffect(() => {
    if (survey && visible && !shownRef.current) {
      shownRef.current = true;
      try {
        posthog.capture("survey shown", { $survey_id: survey.id, $survey_name: survey.name });
      } catch { /* best-effort */ }
    }
  }, [survey, visible]);

  // Response props in BOTH PostHog formats (legacy indexed + modern id-keyed)
  // for whatever subset of questions has been answered so far.
  const buildResponseProps = useCallback(
    (answered: Record<number, string | string[] | number>) => {
      if (!survey) return {};
      const props: Record<string, unknown> = {
        $survey_id: survey.id,
        $survey_name: survey.name,
        $survey_questions: survey.questions.map((q: any) => ({ id: q.id, question: q.question })),
      };
      survey.questions.forEach((q: any, i) => {
        if (!(i in answered)) return;
        props[i === 0 ? "$survey_response" : `$survey_response_${i}`] = answered[i];
        if (q.id) props[`$survey_response_${q.id}`] = answered[i];
      });
      return props;
    },
    [survey],
  );

  const dismiss = useCallback(() => {
    if (!survey) return;
    // Closing the THANK-YOU screen is not a dismissal — the survey was already
    // completed and "survey sent" already fired. Just close quietly.
    // (v1 logged a "survey dismissed" 1s after "survey sent" for anyone who
    // closed the thanks card manually, polluting the dismissal stats.)
    if (thanks) {
      setVisible(false);
      return;
    }
    try {
      localStorage.setItem(SEEN_KEY(survey.id), "dismissed");
      // PARTIAL CAPTURE: if they answered some questions before closing, ship
      // what we have on the dismissed event — $survey_partially_completed lets
      // downstream (Discord notifications etc.) distinguish "answered 3 of 6
      // then bailed" from "closed it instantly".
      const answeredCount = Object.keys(responses).length;
      posthog.capture("survey dismissed", {
        ...buildResponseProps(responses),
        $survey_partially_completed: answeredCount > 0,
        $survey_questions_answered: answeredCount,
      });
    } catch { /* best-effort */ }
    setVisible(false);
  }, [survey, responses, buildResponseProps, thanks]);

  const submitAll = useCallback(
    (finalResponses: Record<number, string | string[] | number>) => {
      if (!survey) return;
      try {
        localStorage.setItem(SEEN_KEY(survey.id), "answered");
        posthog.capture("survey sent", {
          ...buildResponseProps(finalResponses),
          $survey_completed: true,
        });
      } catch { /* best-effort */ }
      setThanks(true);
      setTimeout(() => setVisible(false), 3500);
    },
    [survey, buildResponseProps],
  );

  const advance = useCallback(
    (answer: string | string[] | number) => {
      if (!survey) return;
      const next = { ...responses, [questionIndex]: answer };
      setResponses(next);
      setOpenText("");
      setMultiPick(new Set());
      if (questionIndex + 1 < survey.questions.length) {
        setQuestionIndex(questionIndex + 1);
      } else {
        submitAll(next);
      }
    },
    [survey, responses, questionIndex, submitAll],
  );

  if (!survey || !visible) return null;

  const q = survey.questions[questionIndex];
  const isLast = questionIndex + 1 >= survey.questions.length;

  return (
    <div className="fixed bottom-24 right-4 z-[54] w-[340px] max-w-[calc(100vw-2rem)] animate-in slide-in-from-bottom-4 fade-in duration-300">
      <div className="rounded-xl border border-gray-200 bg-white shadow-2xl overflow-hidden">
        {/* Header strip — CRM navy */}
        <div className="flex items-center justify-between bg-[#002a5b] px-4 py-2.5">
          <div className="flex items-center gap-2 text-white">
            <MessageSquareHeart className="h-4 w-4" />
            <span className="text-xs font-semibold tracking-wide">Quick question</span>
          </div>
          <button
            type="button"
            onClick={dismiss}
            aria-label="Dismiss survey"
            className="text-white/70 hover:text-white transition-colors"
          >
            <X className="h-4 w-4" />
          </button>
        </div>

        {thanks ? (
          <div className="px-5 py-6 text-center space-y-1">
            <p className="text-base font-semibold text-gray-900">
              {survey.appearance?.thankYouMessageHeader || "Thanks — you're the best 🙌"}
            </p>
            <p className="text-sm text-gray-500">
              {survey.appearance?.thankYouMessageDescription || "Your feedback goes straight to the team."}
            </p>
          </div>
        ) : (
          <div className="px-4 py-4 space-y-3">
            <div>
              <p className="text-sm font-semibold text-gray-900 leading-snug">{q.question}</p>
              {q.description && <p className="text-xs text-gray-500 mt-1">{q.description}</p>}
            </div>

            {/* ── Rating (numbers or emoji) ── */}
            {q.type === "rating" && (
              <div className="space-y-1.5">
                {q.display === "emoji" && EMOJI_SCALES[q.scale ?? 5] ? (
                  <div className="flex gap-1">
                    {EMOJI_SCALES[q.scale ?? 5].map((emoji, i) => (
                      <button
                        key={i}
                        type="button"
                        onClick={() => advance(i + 1)}
                        className="flex-1 rounded-md border border-gray-200 py-1.5 text-xl hover:bg-blue-50 hover:border-[#002a5b] transition-colors"
                      >
                        {emoji}
                      </button>
                    ))}
                  </div>
                ) : (() => {
                  // Scale 10 = NPS convention: ELEVEN buttons, 0 through 10.
                  // Other scales (3/5/7) are 1..N. (v1 drew ten buttons 0-9 —
                  // literally nobody could rate us a 10.)
                  const scale = q.scale ?? 10;
                  const isNps = scale === 10;
                  const values = isNps
                    ? Array.from({ length: 11 }, (_, i) => i)
                    : Array.from({ length: scale }, (_, i) => i + 1);
                  return (
                    <div className={isNps ? "grid grid-cols-11 gap-1" : "flex gap-1"}>
                      {values.map((value) => (
                        <button
                          key={value}
                          type="button"
                          onClick={() => advance(value)}
                          className={cn(
                            "h-8 rounded-md border border-gray-200 font-medium text-gray-700 hover:bg-[#002a5b] hover:text-white hover:border-[#002a5b] transition-colors",
                            isNps ? "min-w-0 px-0 text-xs" : "min-w-8 flex-1 text-sm",
                          )}
                        >
                          {value}
                        </button>
                      ))}
                    </div>
                  );
                })()}
                {(q.lowerBoundLabel || q.upperBoundLabel) && (
                  <div className="flex justify-between text-[10px] text-gray-400">
                    <span>{q.lowerBoundLabel}</span>
                    <span>{q.upperBoundLabel}</span>
                  </div>
                )}
              </div>
            )}

            {/* ── Single choice ── */}
            {q.type === "single_choice" && (
              <div className="space-y-1.5">
                {(q.choices || []).map((choice) => (
                  <button
                    key={choice}
                    type="button"
                    onClick={() => advance(choice)}
                    className="w-full rounded-md border border-gray-200 px-3 py-2 text-left text-sm text-gray-700 hover:bg-blue-50 hover:border-[#002a5b] transition-colors"
                  >
                    {choice}
                  </button>
                ))}
              </div>
            )}

            {/* ── Multiple choice ── */}
            {q.type === "multiple_choice" && (
              <div className="space-y-2">
                <div className="space-y-1.5">
                  {(q.choices || []).map((choice) => {
                    const picked = multiPick.has(choice);
                    return (
                      <button
                        key={choice}
                        type="button"
                        onClick={() => {
                          setMultiPick((prev) => {
                            const next = new Set(prev);
                            if (next.has(choice)) next.delete(choice); else next.add(choice);
                            return next;
                          });
                        }}
                        className={cn(
                          "w-full rounded-md border px-3 py-2 text-left text-sm transition-colors",
                          picked
                            ? "border-[#002a5b] bg-blue-50 text-[#002a5b] font-medium"
                            : "border-gray-200 text-gray-700 hover:bg-gray-50",
                        )}
                      >
                        {choice}
                      </button>
                    );
                  })}
                </div>
                <Button
                  size="sm"
                  className="w-full bg-[#002a5b] hover:bg-[#003a7a]"
                  disabled={multiPick.size === 0}
                  onClick={() => advance(Array.from(multiPick))}
                >
                  {isLast ? "Submit" : "Next"} <ChevronRight className="h-3.5 w-3.5 ml-1" />
                </Button>
              </div>
            )}

            {/* ── Open text ── */}
            {q.type === "open" && (
              <div className="space-y-2">
                <Textarea
                  value={openText}
                  onChange={(e) => setOpenText(e.target.value)}
                  placeholder="Type your thoughts…"
                  rows={3}
                  className="text-sm resize-none"
                />
                <Button
                  size="sm"
                  className="w-full bg-[#002a5b] hover:bg-[#003a7a]"
                  disabled={!q.optional && openText.trim().length === 0}
                  onClick={() => advance(openText.trim())}
                >
                  {isLast ? "Submit" : "Next"} <ChevronRight className="h-3.5 w-3.5 ml-1" />
                </Button>
              </div>
            )}

            {/* ── Link / announcement CTA ── */}
            {q.type === "link" && (
              <Button
                size="sm"
                className="w-full bg-[#002a5b] hover:bg-[#003a7a]"
                onClick={() => {
                  if (q.link) window.open(q.link, "_blank", "noopener");
                  advance("link clicked");
                }}
              >
                {q.buttonText || "Check it out"}
              </Button>
            )}

            {survey.questions.length > 1 && (
              <p className="text-[10px] text-gray-400 text-right">
                {questionIndex + 1} of {survey.questions.length}
              </p>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
